import React, { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Typography, IconButton, useTheme, TextField, Button } from '@mui/material';

import CanvasApi from '../../../services/CanvasApi';
import CloseIcon from '../../../landingPage/assets/icons/CloseIcon';
import { grey } from '@mui/material/colors';
import { GenerateImageResponse } from '../../../types/CanvaGenerateInterface';


export const MODAL_NAME = 'EditImageWithTextModal';
export const EDIT_IMAGE_PROMPT = 'BackgroundPrompt';

interface EditImageWithTextModalProps {
  canvasApi: CanvasApi;
}

const EditImageWithTextModal: React.FC<EditImageWithTextModalProps> = ({ canvasApi }) => {
  const theme = useTheme();
  const [displayModal, setDisplayModal] = useState(false);
  const [editImagePrompt, setEditImagePrompt] = useState<string>();

  canvasApi.registerModalControl(MODAL_NAME, displayModal, setDisplayModal);
  canvasApi.registerComponentState(EDIT_IMAGE_PROMPT, editImagePrompt, setEditImagePrompt);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    canvasApi.hideModal(MODAL_NAME);
  };

  const replaceUsingPrompt = async () => {
    canvasApi.executeImageGeneration(async requestData => ({
      ...requestData,
      prompt: editImagePrompt,
    }), async (client, request) => {
      const { data }: { data: GenerateImageResponse } = await client.editSubjectWithText(request);
      return data;
    }).then(() => {
      setEditImagePrompt(undefined);
    });
    canvasApi.hideAllModals();
  };

  return (
    <Dialog open={displayModal}
      fullWidth={true}
      maxWidth="xl"
      onClose={(event, reason) => handleClose(event, reason)}>
      <DialogTitle>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Typography sx={{
            fontSize: '2rem',
            fontWeight: 'bold',
          }}>Replace Background</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <IconButton
          aria-label="close"
          onClick={() => canvasApi.hideModal(MODAL_NAME)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{
          height: '25vh',
          display: 'flex',
          flexDirection: 'column',
        }}>

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',

          }}>
            <Typography >Edit the image by giving the AI a description:</Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              width: '100%',
              height: '100%',
              p: '1em'
            }}>
              <Box sx={{
                order: '0',
                mb: '1em',
              }}>
              </Box>
              <Box sx={{
                order: '1',
                flex: '1 1 auto',
              }}>
                <TextField
                  id='replace-background-prompt'
                  variant="outlined"
                  fullWidth
                  placeholder="Describe the changes you want to made to the image."
                  value={editImagePrompt || ''}
                  onChange={e => setEditImagePrompt(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      replaceUsingPrompt();
                    }
                  }}
                  multiline
                  rows={4}
                  maxRows={6}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                      fontWeight: theme.typography.fontWeightRegular,
                      padding: theme.spacing(0, 3),
                      '&::placeholder': {
                        fontWeight: theme.typography.fontWeightLight
                      },
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.default} inset`,
                        WebkitTextFillColor: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.default
                      }
                    },
                    '& .MuiOutlinedInput-root': {
                      background: 'black',
                      padding: 1,
                      borderRadius: 7,
                      '& fieldset': {
                        border: `1px solid ${grey[800]}`
                      },
                      '&:hover fieldset': {
                        border: `1px solid ${theme.palette.mode === 'light' ? grey[400] : grey[700]}`
                      },
                      '&.Mui-focused fieldset': {
                        border: `1px solid ${theme.palette.mode === 'light' ? grey[400] : grey[700]}`
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{
                order: '99',
                alignSelf: 'flex-end',
              }}>
                <Button
                  disableElevation
                  size='large'
                  variant="contained"
                  color="secondary"
                  onClick={replaceUsingPrompt}
                  sx={{
                    width: 160,
                    px: 4,
                    textTransform: 'none',
                    borderRadius: 50,
                  }}
                >
                  <Typography fontWeight={'medium'}>Edit Image</Typography>
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog >
  );
};

export default EditImageWithTextModal;